import { getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import Field from "@components/inputs/field";
import { getMoreItemsText, MultiSelect } from "@components/inputs/select/MultiSelect";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { SwitchType } from "@components/inputs/switch/Switch";
import { ifAll, IFieldDef, IFieldDefFn, IFieldInfoProperties, IGetValueArgs } from "@components/smart/FieldInfo";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { IFormGroupDef, TFormTable } from "@components/smart/smartFormGroup/SmartFormGroup";
import { fetchAndSetItemsByInfo } from "@components/smart/smartSelect/SmartSelectAPI";
import { smartStrongTextFormatter } from "@components/smart/smartTable/SmartTable.utils";
import { TCellValue, TId } from "@components/table";
import { ITokenProps } from "@components/token";
import Tokenizer from "@components/token/Tokenizer";
import { getBoundValue } from "@odata/Data.utils";
import { getRouteByDocumentType } from "@odata/EntityTypes";
import {
    CbaCategoryEntity,
    CbaEntryEntity,
    DocumentCbaCategoryEntity,
    DocumentEntity,
    DocumentItemCbaCategoryEntity,
    EntitySetName,
    ICbaCategoryEntity,
    ICbaCategoryTaxImpactEntity,
    ICbaEntryEntity,
    IDocumentCbaCategoryEntity,
    IDocumentItemCbaCategoryEntity,
    IPaymentDocumentEntity,
    PaymentDocumentEntity,
    PaymentDocumentItemEntity,
    RegularDocumentItemEntity
} from "@odata/GeneratedEntityTypes";
import { CbaCategoryTaxImpactCode, DocumentTypeCode } from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import { hasEverBeenVatRegisteredCompany, isCashBasisAccountingCompany } from "@utils/CompanyUtils";
import { isDefined, isNotDefined, isObjectEmpty } from "@utils/general";
import i18next from "i18next";
import React from "react";

import { DASH_CHARACTER } from "../../constants";
import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FieldType, Sort, ValidatorType } from "../../enums";
import { TValue } from "../../global.types";
import BindingContext, { createPath, IEntity } from "../../odata/BindingContext";
import { getCurrentLineItem } from "../../views/formView/Form.utils";
import { FormStorage } from "../../views/formView/FormStorage";
import { IBankCustomData, TPairedDocumentCategoriesMap } from "../banks/bankTransactions/BankTransactions.utils";
import { TFieldsDefinition } from "../PageUtils";

export const CASH_BASIS_ACCOUNTING_GROUP_ID = "CashBasisAccounting";

export const CBA_CATEGORY_PATH = BindingContext.localContext("CategorySelection");

const isCashBasisAccounting = (args: IGetValueArgs): boolean => {
    return isCashBasisAccountingCompany(args.context ?? args.storage.context);
};

const hasPartialImpact = (args: IGetValueArgs) => {
    const category = args.storage.getValue(args.bindingContext.getParent()) as ICbaCategoryEntity;
    const code = args.storage.getTemporalData(args.bindingContext.getParent().navigate(CbaCategoryEntity.TaxImpact))?.value ?? category?.TaxImpact?.Code;
    return code === CbaCategoryTaxImpactCode.Partial;
};

const hasNoImpact = (args: IGetValueArgs) => {
    const category = args.storage.getValue(args.bindingContext.getParent()) as ICbaCategoryEntity;
    const code = args.storage.getTemporalData(args.bindingContext.getParent().navigate(CbaCategoryEntity.TaxImpact))?.value ?? category?.TaxImpact?.Code;
    return code === CbaCategoryTaxImpactCode.Nontax;
};

interface ICbaItemCategorySettings {
    isDefault: boolean;
    TaxImpact?: ICbaCategoryTaxImpactEntity;
    TaxImpactCode: CbaCategoryTaxImpactCode;
    CategoryId?: number;
    CategoryName?: string;
    Category?: ICbaCategoryEntity;
    IsAssetAcquisition?: boolean;
    TaxPercentage?: number;
}

// function returns CBA Item category settings
export function getCBAItemCategory(storage: FormStorage, itemBc: BindingContext, hasDefaultCategory = true): ICbaItemCategorySettings {
    const itemCategoryBc = itemBc.navigate(PaymentDocumentItemEntity.CbaCategory);
    const itemCategory = storage.getValue(itemCategoryBc, { skipTemporaryValue: true });
    const isDefault = isObjectEmpty(itemCategory) && hasDefaultCategory;

    const settings: ICbaItemCategorySettings = {
        isDefault,
        TaxImpactCode: CbaCategoryTaxImpactCode.Tax
    };

    const { entity } = storage.data;

    if (!isDefault || hasDefaultCategory) {
        const category = (isDefault ? entity.CbaCategory : itemCategory) ?? {};
        settings.TaxImpactCode = category.TaxImpact?.Code ?? category.TaxImpactCode;
        settings.CategoryId = category.Category?.Id;
        settings.CategoryName = category.Category?.Name;
        settings.IsAssetAcquisition = !!category.IsAssetAcquisition;
        settings.TaxPercentage = category.TaxPercentage ?? (settings.TaxImpactCode === CbaCategoryTaxImpactCode.Nontax ? 0 : 100);
    }

    return settings;
}

export const getCashBasisAccountingGroup = (): IFormGroupDef => {
    return {
        id: CASH_BASIS_ACCOUNTING_GROUP_ID,
        isVisible: isCashBasisAccounting,
        customizationData: {
            useForCustomization: isCashBasisAccounting
        },
        rows: [[{ id: createPath(DocumentEntity.CbaCategory, DocumentCbaCategoryEntity.Category) }],
            [{ id: createPath(DocumentEntity.CbaCategory, DocumentCbaCategoryEntity.TaxImpact) }, {
                id: createPath(DocumentEntity.CbaCategory, DocumentCbaCategoryEntity.IsAssetAcquisition)
            }, {
                id: createPath(DocumentEntity.CbaCategory, DocumentCbaCategoryEntity.TaxPercentage)
            }]],
        title: i18next.t("Document:FormGroup.CashBasisAccounting")
    };
};

const getFieldId = (id: string, prefix: string) => {
    return `${prefix ? prefix + "/" : ""}${id}`;
};

export const getCbaCategoriesCommonFieldsDef = (prefix = "", isCollection: boolean, onlySomeFields: boolean): Record<string, IFieldInfoProperties> => {
    return {
        [getFieldId(CbaCategoryEntity.IsAssetAcquisition, prefix)]: {
            type: FieldType.Switch,
            clearIfInvisible: false,
            fieldSettings: {
                type: SwitchType.Default
            },
            defaultValue: false,
            isVisible: (args: IGetValueArgs) => !onlySomeFields && hasNoImpact(args),
            customizationData: {
                useForCustomization: ifAll(!onlySomeFields, !isCollection)
            },
            isConfirmable: !!isCollection
        },
        [getFieldId(CbaCategoryEntity.TaxImpact, prefix)]: {
            type: FieldType.SegmentedButton,
            width: BasicInputSizes.XL,
            label: i18next.t(`Categories:${isCollection ? "" : "Default"}TaxImpact`),
            isRequired: false, // is required, but we don't want red dot TODO: maybe there should not be red dot on any segmented button field
            fieldSettings: {
                items: [
                    {
                        id: CbaCategoryTaxImpactCode.Tax,
                        label: i18next.t("Categories:CbaCategoryTaxImpactCode.Tax")
                    },
                    {
                        id: CbaCategoryTaxImpactCode.Nontax,
                        label: i18next.t("Categories:CbaCategoryTaxImpactCode.NonTax")
                    },
                    {
                        id: CbaCategoryTaxImpactCode.Partial,
                        label: i18next.t("Categories:CbaCategoryTaxImpactCode.Partial")
                    }
                ]
            },
            defaultValue: CbaCategoryTaxImpactCode.Tax,
            isVisible: !onlySomeFields,
            customizationData: {
                useForCustomization: ifAll(!onlySomeFields, !isCollection)
            },
            isConfirmable: !!isCollection
        },
        [getFieldId(CbaCategoryEntity.TaxPercentage, prefix)]: {
            type: FieldType.NumberInput,
            fieldSettings: {
                unit: "%",
                showSteppers: true,
                min: 0,
                max: 100
            },
            defaultValue: 100,
            validator: {
                type: ValidatorType.Number,
                settings: {
                    min: 0,
                    max: 100
                }
            },
            isVisible: (args) => !onlySomeFields && hasPartialImpact(args),
            customizationData: {
                useForCustomization: ifAll(!onlySomeFields, !isCollection)
            },
            isConfirmable: !!isCollection
        }
    };
};

const categoriesFilterByTypeFn = (onlySomeFields: boolean) => {
    return () => {
        let filter = `${CbaCategoryEntity.IsActive} eq true`;
        if (onlySomeFields) {
            filter += ` AND ${CbaCategoryEntity.TaxImpactCode} eq '${CbaCategoryTaxImpactCode.Tax}'`;
        }
        return filter;
    };
};

export const getCashBasisAccountingFieldsDef = (prefix: string = DocumentEntity.CbaCategory, isCollection: boolean, onlySomeFields: boolean): TFieldsDefinition => {
    return {
        [`${prefix ? prefix + "/" : ""}Category`]: {
            type: FieldType.ComboBox,
            label: i18next.t(`Categories:${isCollection ? "" : "Default"}Category`),
            filter: {
                select: categoriesFilterByTypeFn(onlySomeFields)
            },
            fieldSettings: {
                displayName: "Name",
                additionalProperties: [{
                    id: CbaCategoryEntity.TaxPercentage
                }, {
                    id: CbaCategoryEntity.TaxImpact
                }, {
                    id: CbaCategoryEntity.IsAssetAcquisition
                }],
                additionalItems: [{
                    id: null,
                    label: i18next.t("Categories:WithoutCategory")
                }]
            },
            formatter: (val: TValue, args: IFormatOptions): string => {
                return args.item?.Name ?? args.storage.getTemporalData(args.bindingContext)?.additionalData?.Name ?? i18next.t("Categories:WithoutCategory").toString();
            },
            defaultValue: null,
            customizationData: {
                useForCustomization: !isCollection
            },
            isConfirmable: !!isCollection
        },
        ...getCbaCategoriesCommonFieldsDef(prefix, isCollection, onlySomeFields)
    };
};

export enum CbaSelectionCode {
    Default = "Default",
    Own = "Own"
}

export async function setCorrectSpecialItemsForItemCategory(storage: FormStorage, collectionName = "Items"): Promise<void> {
    if (!isCashBasisAccountingCompany(storage.context)) {
        return;
    }
    const baseBc = storage.data.bindingContext.navigate(collectionName);
    const info = storage.getInfo(baseBc.navigate(CBA_CATEGORY_PATH));
    const items = await fetchAndSetItemsByInfo(storage, info, false);

    for (const item of (storage.data.entity[collectionName] || [])) {
        const itemBc = baseBc.addKey(item);
        const itemCategory = getCBAItemCategory(storage, itemBc, true);
        let value: TId = CbaSelectionCode.Default;
        if (!itemCategory.isDefault) {
            if (!itemCategory.CategoryId && !itemCategory.IsAssetAcquisition && [CbaCategoryTaxImpactCode.Tax, CbaCategoryTaxImpactCode.Nontax, CbaCategoryTaxImpactCode.VATCorrection].includes(itemCategory.TaxImpactCode)) {
                value = itemCategory.TaxImpactCode;
            } else {
                // find item with same properties
                const matchingItem: ISelectItem = items.find(item => {
                    const data = item.additionalData ?? {};
                    return data?.TaxImpactCode === itemCategory.TaxImpactCode && data.Id === itemCategory.CategoryId &&
                        data.TaxPercentage === itemCategory.TaxPercentage && data.IsAssetAcquisition === itemCategory.IsAssetAcquisition;
                });
                value = matchingItem?.id ?? CbaSelectionCode.Own;
            }
        }
        storage.setValue(itemBc.navigate(CBA_CATEGORY_PATH), value);
    }
}

export function getPartialImpactLabel(impact: string, percentage: number): string {
    return `${impact}: ${percentage} %`;
}

export function getAssetAcquisitionLabel(impact: string): string {
    return `${impact}: ${i18next.t("Categories:AcquisitionOfAssets")}`;
}

function getLinkedDocument(storage: FormStorage, bindingContext: BindingContext, isBank: boolean) {
    const isInternalDoc = storage.data.entity.DocumentTypeCode === DocumentTypeCode.InternalDocument;
    const collectionBc = bindingContext.getParent();
    let linkedDoc = null;
    if (isInternalDoc || isBank) {
        const linkedDocBc = collectionBc.navigate(PaymentDocumentItemEntity.LinkedDocument);
        linkedDoc = storage.getValue(linkedDocBc);
    }
    return linkedDoc;
}

function formatCbaImpactLabel(cba: ICbaItemCategorySettings) {
    const code = cba.TaxImpactCode ?? cba.TaxImpact?.Code;
    if (!code) {
        return "";
    }
    let impactLabel = i18next.t(`Categories:CbaCategoryTaxImpactCode.${code}`);
    if (cba.TaxImpactCode === CbaCategoryTaxImpactCode.Partial) {
        impactLabel = getPartialImpactLabel(impactLabel, cba.TaxPercentage);
    } else if (cba.IsAssetAcquisition) {
        impactLabel = getAssetAcquisitionLabel(impactLabel);
    }
    return impactLabel;
}

function getCbaLabelSuffix(cba: ICbaItemCategorySettings): string {
    const name = cba.CategoryName ?? cba.Category?.Name;
    return name ? ` | ${name}` : "";
}

export const getCbaCategoryItemDef = (collectionName: string, isBank: boolean, onlySomeFields: boolean): TFieldsDefinition => {
    const def: TFieldsDefinition = {
        [createPath(collectionName, CBA_CATEGORY_PATH)]: {
            type: FieldType.ComboBox,
            label: i18next.t("Categories:Records"),
            isRequired: true,
            useForValidation: false,
            width: isBank ? BasicInputSizes.XL : BasicInputSizes.L,
            isDisabled: args => {
                const categoryBc = args.bindingContext.getParent().navigate(DocumentEntity.CbaCategory);
                return !!(args.storage as FormStorage).getBackendDisabledFieldMetadata(categoryBc);
            },
            filter: {
                select: categoriesFilterByTypeFn(onlySomeFields)
            },
            fieldSettings: {
                displayName: "TaxImpact/Name",
                entitySet: EntitySetName.CbaCategories,
                additionalProperties: [{
                    id: CbaCategoryEntity.TaxPercentage
                }, {
                    id: CbaCategoryEntity.TaxImpact
                }, {
                    id: CbaCategoryEntity.IsAssetAcquisition
                }],
                additionalItems: [{
                    id: CbaCategoryTaxImpactCode.Tax,
                    label: i18next.t("Categories:CbaCategoryTaxImpactCode.Tax")
                },
                    ...(onlySomeFields ? [] : [
                        {
                            id: CbaCategoryTaxImpactCode.Nontax,
                            label: i18next.t("Categories:CbaCategoryTaxImpactCode.NonTax")
                        },
                        {
                            id: CbaSelectionCode.Own,
                            label: i18next.t("Categories:CbaSelectionCode.Own")
                        }
                    ])]
            },
            customizationData: {
                useForCustomization: isCashBasisAccounting
            },
            comparisonFunction: (entity1: IEntity, entity2: IEntity, bc: BindingContext): boolean => {
                const categoryBc = bc.getParent().navigate(RegularDocumentItemEntity.CbaCategory);
                const dataBindingContext = bc.getRootParent();

                const cat1 = getBoundValue({ bindingContext: categoryBc, data: entity1, dataBindingContext });
                const cat2 = getBoundValue({ bindingContext: categoryBc, data: entity2, dataBindingContext });

                if (isObjectEmpty(cat1) && isObjectEmpty(cat2)) {
                    // both categories are not defined -> they are same
                    return true;
                }

                // if one is defined and second not, or they have different impact code => categories are not same
                if (isObjectEmpty(cat1) !== isObjectEmpty(cat2) || cat1?.TaxImpact?.Code !== cat2?.TaxImpact?.Code) {
                    return false;
                }

                // both defined and code is same -> compare additional properties
                return (cat1.IsAssetAcquisition === cat2.IsAssetAcquisition) && (cat1.TaxPercentage === cat2.TaxPercentage);
            },
            formatter: (val: TValue, { storage, bindingContext, item }: IFormatOptions): string => {
                const cbaSettings = bindingContext ? getCBAItemCategory(storage as FormStorage, bindingContext.getParent(), !isBank)
                    // formatter is called also for select items, which can be used as cbaSettings
                    : item as ICbaItemCategorySettings;
                const prefix = cbaSettings.isDefault ? `(${i18next.t("Categories:CbaSelectionCode.Default")}) ` : "";
                const impactLabel = formatCbaImpactLabel(cbaSettings);
                const suffix = getCbaLabelSuffix(cbaSettings);

                return `${prefix}${impactLabel}${suffix}`;
            },
            columns: [
                {
                    id: "TaxImpact/Name",
                    formatter: (val: TValue, args?: IFormatOptions) =>
                        formatCbaImpactLabel(args.item as ICbaItemCategorySettings)
                },
                { id: "Name" }
            ],
            defaultValue: null,
            isReadOnly: (args: IGetValueArgs) => {
                const linkedDoc = getLinkedDocument(args.storage as FormStorage, args.bindingContext, isBank);
                return !!linkedDoc?.Id;
            },
            hasOwnReadOnlyMaxWidth: true,
            render: (args: IFieldDefFn) => {
                const linkedDoc = getLinkedDocument(args.storage as FormStorage, args.bindingContext, isBank);
                const isReadOnlyTable = !args.fieldElement;

                if (linkedDoc?.Id) {
                    /**
                     * When there are linked documents, we display multiselect with tokens to display all categories from the linked document entity
                     *  - view only mode (disabled or readonly)
                     */
                    const docCategories = args.storage.getCustomData<IBankCustomData>()?.pairedDocumentCategoriesMap?.[linkedDoc.Id] ?? [];

                    const val = docCategories.map(cat => cat.Id);
                    const preparedItems: ISelectItem[] = docCategories.map((cat): ISelectItem => {
                        const cbaCat = cat as ICbaItemCategorySettings;
                        return {
                            id: cat.Id,
                            label: formatCbaImpactLabel(cbaCat),
                            description: cbaCat.CategoryName ?? cbaCat.Category?.Name,
                        };
                    });

                    const { width, isSharpLeft, isSharpRight, name, isDisabled, isReadOnly, isRequired } = args.props;
                    const passProps = {
                        width, isSharpRight, isSharpLeft, name, isDisabled, isRequired, isReadOnly
                    };

                    if (isReadOnlyTable) {
                        const tokenDefs = preparedItems.map((item): ITokenProps => ({
                            ...item,
                            id: item.id.toString(),
                            title: item.label
                        }));
                        return {
                            value: (
                                <Tokenizer tokens={tokenDefs}
                                           customMoreTokensText={getMoreItemsText}
                                           showMoreTokensTooltip
                                           maxVisibleTokens={1} />
                            ),
                            tooltip: null
                        };
                    }

                    return (
                        <Field label={i18next.t("Categories:Records")}
                               useWidthWhenReadOnly
                               {...passProps}
                               labelStatus={args.props.labelStatus}>
                            <MultiSelect {...passProps}
                                         items={preparedItems}
                                         value={val}
                                         maxVisibleTokens={1} />
                        </Field>
                    );
                }
                if (isReadOnlyTable) {
                    return args.props.formattedValue;
                }
                return args.fieldElement;
            }
        },
        ...getCashBasisAccountingFieldsDef(createPath(collectionName, RegularDocumentItemEntity.CbaCategory), true, onlySomeFields)
    };

    if (!isBank) {
        def[createPath(collectionName, CBA_CATEGORY_PATH)].fieldSettings.additionalItems = [
            {
                id: CbaSelectionCode.Default,
                label: i18next.t("Categories:CbaSelectionCode.Default")
            },
            ...def[createPath(collectionName, CBA_CATEGORY_PATH)].fieldSettings.additionalItems
        ];
    }
    return def;
};

export const getDefaultPercetageFromImpactCode = (code: CbaCategoryTaxImpactCode): number => {
    switch (code) {
        case CbaCategoryTaxImpactCode.Tax:
        case CbaCategoryTaxImpactCode.VATCorrection:
            return 100;
        case CbaCategoryTaxImpactCode.Nontax:
            return 0;
        case CbaCategoryTaxImpactCode.Partial:
            return 60;
    }
};

export function categoryFormatter(value: TValue): TCellValue {
    if (!value) {
        return DASH_CHARACTER;
    }
    return value as string;
}

export const getCbaEntriesTable = (documentType: DocumentTypeCode, context: IAppContext): TFormTable => {
    const isInternalDoc = documentType === DocumentTypeCode.InternalDocument;
    const isBankTransaction = documentType === DocumentTypeCode.BankTransaction ||
        documentType === DocumentTypeCode.CashReceiptIssued || documentType === DocumentTypeCode.CashReceiptReceived;
    const isLoanTransaction = documentType === DocumentTypeCode.LoanTransaction;
    const isVatRegistered = hasEverBeenVatRegisteredCompany(context);

    const paymentDocumentDef = {
        id: CbaEntryEntity.PaymentDocument,
        fieldSettings: {
            displayName: PaymentDocumentEntity.NumberOurs
        },
        additionalProperties: [
            { id: `/${createPath(CbaEntryEntity.InternalDocument, DocumentEntity.NumberOurs)}` },
            { id: `/${createPath(CbaEntryEntity.InternalDocument, DocumentEntity.DocumentType)}` },
            { id: `/${createPath(CbaEntryEntity.PaymentDocument, PaymentDocumentEntity.DocumentType)}` },
            { id: `/${createPath(CbaEntryEntity.ProformaDocument, DocumentEntity.DocumentType)}` },
            { id: `/${createPath(CbaEntryEntity.ProformaDocument, DocumentEntity.NumberOurs)}` }
        ],
        formatter: (val: TValue, args: IFormatOptions) => {
            const entity = args.entity as ICbaEntryEntity;
            const doc = [entity.PaymentDocument, entity.ProformaDocument, entity.InternalDocument].find(doc => !!doc?.Id);

            return getTableIntentLink(doc.NumberOurs, {
                route: `${getRouteByDocumentType(doc?.DocumentType?.Code as DocumentTypeCode)}/${doc.Id}`,
                context: args.storage.context,
                storage: args.storage
            });
        },
        label: i18next.t("Document:PairedDocuments.Document")
    };
    const pairedDocumentDef = {
        id: CbaEntryEntity.PairedDocument,
        fieldSettings: {
            displayName: "NumberOurs"
        },
        label: i18next.t("Document:PairedDocuments.Document")
    };

    const columns: IFieldDef[] = [
        { id: CbaEntryEntity.Date },
        !isLoanTransaction && !isInternalDoc && (isBankTransaction ? pairedDocumentDef : paymentDocumentDef),
        {
            id: CbaEntryEntity.Type,
            fieldSettings: {
                displayName: "Name"
            }
        },
        !isInternalDoc && {
            id: CbaEntryEntity.TransactionAmountCleared,
            formatter: smartStrongTextFormatter
        },
        { id: CbaEntryEntity.TransactionAmountTaxed },
        { id: CbaEntryEntity.TransactionAmountNonTaxed },
        ...(!isVatRegistered ? [] : [{ id: CbaEntryEntity.TransactionAmountVat }]),
        {
            id: CbaEntryEntity.Category,
            fieldSettings: {
                displayName: "Name"
            },
            formatter: categoryFormatter
        },
        { id: CbaEntryEntity.Description }
    ].filter(item => !!item);

    const additionalProps = isBankTransaction || isLoanTransaction ? {
        filter: (args: IGetValueArgs) => {
            const docId = args.storage.data.bindingContext.getKey();
            const entityName = isInternalDoc ? 'InternalDocument' : 'PaymentDocument';

            return `${entityName}/Id eq ${docId}`;
        }
    } : { parentKey: "PairedDocument/Id" };

    return {
        id: `cbaLedger`,
        entitySet: EntitySetName.CbaEntries,
        ...additionalProps,
        initialSortBy: [{ id: "Date", sort: Sort.Desc }],
        columns
    };
};

export const loadCategoriesMap = (storage: FormStorage<IEntity, IBankCustomData>): void => {
    const entity = storage.data.entity as IPaymentDocumentEntity;
    const pairedItems = entity.Items?.filter(i => !isObjectEmpty(i.LinkedDocument));
    const pairedDocumentCategoriesMap: TPairedDocumentCategoriesMap = {};

    for (const item of (pairedItems ?? [])) {
        let defaultInserted = false;
        const linkedDocument = item.LinkedDocument;
        const categories: (IDocumentCbaCategoryEntity | IDocumentItemCbaCategoryEntity)[] = [];
        if (linkedDocument.DocumentItems) {
            for (const docItem of linkedDocument.DocumentItems) {
                if (isObjectEmpty(docItem.CbaCategory)) {
                    if (!defaultInserted) {
                        if (!categories.some(c => isSameCbaCategory(linkedDocument.CbaCategory, c))) {
                            categories.push(linkedDocument.CbaCategory);
                        }
                        defaultInserted = true;
                    }
                } else {
                    if (!categories.some(c => isSameCbaCategory(docItem.CbaCategory, c))) {
                        categories.push(docItem.CbaCategory);
                    }
                }
            }
        }
        pairedDocumentCategoriesMap[linkedDocument.Id] = categories;
        storage.setCustomData({ pairedDocumentCategoriesMap });
    }
};

// moved from DocumentFormView
// TODO: temp hotfix - For documents created from ISDOC, some fields might be missing -> calculate/default them if possible
// also in DraftMassAccountingDialogFormView, we need to set the default values even thought the fields are not visible
export const setCbaDefaultValuesForNotVisibleFields = (storage: FormStorage): void => {
    if (isNotDefined(storage.data.entity.CbaCategory.TaxImpact)) {
        const taxImpactBc = storage.data.bindingContext.navigate(`${DocumentEntity.CbaCategory}/${DocumentCbaCategoryEntity.TaxImpact}`);
        storage.setDefaultValue(taxImpactBc);
    }
    if (isNotDefined(storage.data.entity.CbaCategory.TaxPercentage)) {
        const taxPercentageBc = storage.data.bindingContext.navigate(`${DocumentEntity.CbaCategory}/${DocumentCbaCategoryEntity.TaxPercentage}`);
        storage.setDefaultValue(taxPercentageBc);
    }
    if (isNotDefined(storage.data.entity.CbaCategory.IsAssetAcquisition)) {
        const isAssetAcquisitionBc = storage.data.bindingContext.navigate(`${DocumentEntity.CbaCategory}/${DocumentCbaCategoryEntity.IsAssetAcquisition}`);
        storage.setDefaultValue(isAssetAcquisitionBc);
    }
};

export const handleItemCbaCategoryChange = (args: ISmartFieldChange, storage: FormStorage<IEntity, IBankCustomData>): void => {
    if (args.bindingContext.getPath() === CBA_CATEGORY_PATH && args.triggerAdditionalTasks) {
        const itemBc = args.bindingContext.getParent();
        const catBc = itemBc.navigate(PaymentDocumentItemEntity.CbaCategory);
        let category: IDocumentCbaCategoryEntity = {};
        if (args.value === CbaSelectionCode.Default) {
            category = null;
        } else if (args.value === CbaSelectionCode.Own) {
            const dialogCurrentLineItem = getCurrentLineItem(storage, args.bindingContext.getParent());
            storage.setCustomData({
                dialogCurrentLineItem,
                isCbaCategoryDialogOpen: true
            });
            const origCat = storage.getValue(catBc);
            storage.setTemporalData(catBc.navigate(DocumentItemCbaCategoryEntity.TaxPercentage), { value: origCat?.TaxPercentage ?? 100 });
            storage.setTemporalData(catBc.navigate(DocumentItemCbaCategoryEntity.TaxImpact), { value: origCat?.TaxImpact?.Code ?? origCat?.TaxImpactCode ?? CbaCategoryTaxImpactCode.Tax });
            storage.setTemporalData(catBc.navigate(DocumentItemCbaCategoryEntity.IsAssetAcquisition), { value: isDefined(origCat?.IsAssetAcquisition) ? origCat?.IsAssetAcquisition : false });
            storage.refresh();
            return;
        } else if (args.value === CbaCategoryTaxImpactCode.Tax || args.value === CbaCategoryTaxImpactCode.VATCorrection) {
            category = {
                TaxPercentage: 100,
                TaxImpactCode: args.value,
                Category: null
            };
        } else if (args.value === CbaCategoryTaxImpactCode.Nontax) {
            category = {
                TaxPercentage: 0,
                TaxImpactCode: CbaCategoryTaxImpactCode.Nontax,
                Category: null
            };
        } else {
            category = {
                TaxPercentage: args.additionalData?.TaxPercentage,
                TaxImpactCode: args.additionalData?.TaxImpactCode,
                IsAssetAcquisition: args.additionalData?.IsAssetAcquisition,
                Category: args.additionalData
            };
        }
        storage.setValue(catBc, category);
    }
};

export const handleCustomCbaCategoryChange = (e: ISmartFieldChange, storage: FormStorage): void => {
    const itemCategoryBc = e.bindingContext.getParent();
    if (e.bindingContext.getPath() === DocumentItemCbaCategoryEntity.Category) {
        const cbaCategory: Partial<ICbaCategoryEntity> = e.additionalData;
        if (!isObjectEmpty(cbaCategory)) {
            storage.setTemporalData(itemCategoryBc.navigate(DocumentItemCbaCategoryEntity.TaxPercentage), { value: cbaCategory.TaxPercentage });
            storage.setTemporalData(itemCategoryBc.navigate(DocumentItemCbaCategoryEntity.TaxImpact), { value: cbaCategory.TaxImpact?.Code });
            storage.setTemporalData(itemCategoryBc.navigate(DocumentItemCbaCategoryEntity.IsAssetAcquisition), { value: cbaCategory.IsAssetAcquisition });
        }
    }
    if (e.bindingContext.getPath() === DocumentItemCbaCategoryEntity.TaxImpact) {
        if (e.value !== CbaCategoryTaxImpactCode.Nontax) {
            storage.setTemporalData(itemCategoryBc.navigate(DocumentItemCbaCategoryEntity.IsAssetAcquisition), { value: false });
        }

        switch (e.value) {
            case CbaCategoryTaxImpactCode.Partial:
                storage.setTemporalData(itemCategoryBc.navigate(DocumentItemCbaCategoryEntity.TaxPercentage), { value: 60 });
                break;
            case CbaCategoryTaxImpactCode.Tax:
            case CbaCategoryTaxImpactCode.VATCorrection:
                storage.setTemporalData(itemCategoryBc.navigate(DocumentItemCbaCategoryEntity.TaxPercentage), { value: 100 });
                break;
            case CbaCategoryTaxImpactCode.Nontax:
                storage.setTemporalData(itemCategoryBc.navigate(DocumentItemCbaCategoryEntity.TaxPercentage), { value: 0 });
                break;
            default:
                break;
        }
    }

    // storage refresh is needed to update related fields (https://solitea-cz.atlassian.net/browse/DEV-28647)
    storage.refresh();
};

export const isSameCbaCategory = (c1: IDocumentCbaCategoryEntity, c2: IDocumentCbaCategoryEntity): boolean => {
    return c1?.TaxImpact?.Code === c2?.TaxImpact?.Code && c1?.Category?.Name === c2?.Category?.Name && c1?.TaxPercentage === c2?.TaxPercentage && c1?.IsAssetAcquisition === c2?.IsAssetAcquisition;
};